import { PropsWithChildren, useMemo, useState } from 'react';
import { endOfDay, endOfYesterday, startOfDay, startOfMonth } from 'date-fns';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  Currency,
  InstrumentKindLiquidity,
  InstrumentKindVolumes,
  Protocol,
  Venue,
} from '#/leaderboard/entities/shared';
import { StrategyShortCode } from '#/shared/strategies';
import useParsedStoredEntry from '#/store/useParsedStoredEntry';
import { Maybe } from '@paradigm/utils/types';
import { RelativeDate, getPresets } from '#/leaderboard/contexts/date-presets';

export interface FilterState {
  readonly currencies: readonly Currency[];
  readonly instrumentKinds:
    | readonly InstrumentKindVolumes[]
    | readonly InstrumentKindLiquidity[];
  readonly venues: readonly Venue[];
  readonly protocols: readonly Protocol[];
  readonly start: number | null;
  readonly end: number | null;
  readonly strategies: StrategyShortCode[];
  readonly relativeDate: RelativeDate | null;
}

interface FormOption<T = string | null> {
  readonly value: T;
  readonly label: string;
}

export interface FilterActions {
  changeFilters(filter: Partial<FilterState>): void;
  selectOption<
    Field extends keyof FilterState,
    FieldValue = FilterState[Field],
  >(
    field: Field,
    formOptions: ReadonlyArray<
      FormOption<FieldValue extends unknown[] ? FieldValue[number] : FieldValue>
    >,
  ): void;
  selectDates(
    dateRange: Maybe<[Date | null, Date | null]>,
    relativeDate?: Maybe<RelativeDate>,
  ): void;
  reset(): void;
  hidrateStoredFilters(): void;
  getStoredFilters(): FilterState | null;
}

const getEmptyFilterState = (): FilterState => {
  return {
    currencies: [],
    relativeDate: 'MTD',
    instrumentKinds: [],
    venues: [],
    protocols: [],
    strategies: [],
    start: null,
    end: null,
  };
};

const getInitialFilterState = (): FilterState => {
  const today = new Date();
  return {
    ...getEmptyFilterState(),
    start: startOfMonth(today).getTime(),
    end: endOfYesterday().getTime(),
  };
};

/**
 * Check if user selected relative date and normalize with equivalent
 * date given the current day
 */
const normalizeStoredFilters = (
  storedFilter: FilterState | null,
): FilterState | null => {
  if (storedFilter == null) return null;
  if (storedFilter.relativeDate == null) return storedFilter;

  const preset = getPresets(null).find(
    ({ label }) => label === storedFilter.relativeDate,
  );
  if (preset == null) return storedFilter;
  return {
    ...storedFilter,
    start: preset.value[0]?.getTime() ?? null,
    end: preset.value[1]?.getTime() ?? null,
  };
};

interface FactoryArgs {
  readonly key: string;
}

export default function createLeaderboardFilterProvider({ key }: FactoryArgs) {
  const [useFilterState, FilterStateProvider] = createSafeContext<FilterState>(
    `${key} FilterState`,
  );

  const [useFilterActions, FilterActionsProvider] =
    createSafeContext<FilterActions>(`${key} FilterActions`);

  const Provider = ({ children }: PropsWithChildren) => {
    const initialFilterState = getInitialFilterState();
    const storedFilters = useParsedStoredEntry<FilterState>(
      `leaderboard-${key}`,
    );
    const [filters, setFilters] = useState<FilterState>(
      normalizeStoredFilters(storedFilters.get()) ?? initialFilterState,
    );

    const actions: FilterActions = useMemo(
      () => ({
        reset: () => setFilters(getEmptyFilterState()),
        changeFilters: (partialFilter) => {
          setFilters((prevFilter) => ({ ...prevFilter, ...partialFilter }));
        },
        selectOption: (field, formOptions) => {
          actions.changeFilters({
            [field]: formOptions.map((o) => o.value),
          });
        },
        selectDates: (dates, relativeDate) => {
          const [startDate, endDate] = dates ?? [];
          actions.changeFilters({
            start: startDate != null ? startOfDay(startDate).getTime() : null,
            end: endDate != null ? endOfDay(endDate).getTime() : null,
            relativeDate,
          });
        },
        hidrateStoredFilters: () => storedFilters.set(filters),
        getStoredFilters: storedFilters.get,
      }),
      [filters, storedFilters],
    );

    return (
      <FilterStateProvider value={filters}>
        <FilterActionsProvider value={actions}>
          {children}
        </FilterActionsProvider>
      </FilterStateProvider>
    );
  };

  return [Provider, useFilterState, useFilterActions] as const;
}
